import React from 'react';
import { Button, Checkbox, FormField } from '@parsina/framework';
import { C } from '../../../constants/theme';
import { useTranslation } from '../../../i18n';
import ChannelIcon from '../../../components/common/ChannelIcon';
import { CHANNELS, channelOffered, smsSegments } from '../../../lib/messaging';
import {
  NOTIFY_BODY_MAX, NOTIFY_CHANNEL_ORDER, orderChannels, whatsappBlocked, withChannel,
} from '../../../lib/appointmentNotifications';
import useWhatsappSession from '../../../hooks/useWhatsappSession';
import { useClinicCapabilities } from '../../../hooks/useClinicCapabilities';

/**
 * «ارسال اعلان به بیمار» — one compact row of channel ticks at the foot of a booking.
 *
 * The caller owns the value and prefills it from «تنظیمات › اعلان‌ها»; this row only
 * renders it. One tick per channel, in the order «ارسال پیام» lists its channel cards,
 * each a real checkbox — Tab reaches it, Space flips it, and Enter still submits the
 * form, so the booking stays keyboard-only (clinic-app/CLAUDE.md §4.2).
 *
 * A channel that cannot send right now is shown disabled with the reason beside it,
 * and its tick is DROPPED rather than kept: a patient told nothing because the clinic's
 * WhatsApp was unpaired is a surprise the toast should never be the first to break.
 *
 * A channel the clinic's COUNTRY does not offer (PM-0218 — an Iraqi clinic has no
 * Bale) is not listed at all, and a default tick for it is dropped the same way: the
 * BFF refuses the whole booking over a channel the country does not offer.
 *
 * «ویرایش متن پیام» (PM-0556): with a `template` (the text the BFF would send, tokens
 * unsubstituted — `noticeBody`) and `onTextChange`, the desk may open the text and
 * change it for THIS notice only. `text` null = untouched; the caller sends it through
 * `notifyBodyToSend`, so an editor opened and left alone changes nothing. Offered only
 * while a channel is ticked — with nothing to send there is no text to edit.
 */
export default function NotifyPatientField({
  value, onChange, disabled = false, disabledHint = null,
  template = '', text = null, onTextChange = null,
}) {
  const { t, formatDigits } = useTranslation();
  const { session } = useWhatsappSession();
  const capabilities = useClinicCapabilities();
  const waBlocked = whatsappBlocked(session);
  const channels = value || [];
  const offered = NOTIFY_CHANNEL_ORDER.filter((key) => channelOffered(capabilities, key));

  React.useEffect(() => {
    const keep = (key) => channelOffered(capabilities, key) && !(key === 'whatsapp' && waBlocked);
    if (channels.some((key) => !keep(key))) onChange(orderChannels(channels.filter(keep)));
    // Only when a verdict changes — the session's or the country's — not on every
    // tick the desk makes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waBlocked, capabilities]);

  const hint = disabled ? disabledHint : (waBlocked ? t('reception.notify.whatsappNotLinked') : null);
  const editable = !!onTextChange && !!template && !disabled && channels.length > 0;
  const editing = editable && text !== null;
  // Over the tokens, not the names that replace them: «حدود», never a promise.
  const parts = editing ? smsSegments(text).segments : 0;

  return (
    <FormField label={t('reception.notify.label')}>
      <div
        role="group"
        aria-label={t('reception.notify.label')}
        data-testid="booking-notify"
        data-state={disabled ? 'disabled' : 'idle'}
        style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 18, rowGap: 8 }}
      >
        {offered.map((key) => {
          const off = disabled || (key === 'whatsapp' && waBlocked);
          return (
            <Checkbox
              key={key}
              checked={!off && channels.includes(key)}
              disabled={off}
              onChange={(next) => onChange(withChannel(channels, key, next === true))}
              data-testid={`booking-notify-${key}`}
              label={(
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <ChannelIcon channel={key} size={14} color={C.textMuted} />
                  {t(CHANNELS[key].labelKey)}
                </span>
              )}
            />
          );
        })}
      </div>
      {hint ? (
        <div data-testid="booking-notify-hint" style={{ fontSize: '0.71875rem', color: C.textMuted, marginTop: 6, lineHeight: 1.7 }}>
          {hint}
        </div>
      ) : null}
      {editable && !editing ? (
        <Button variant="link" size="sm" onClick={() => onTextChange(template)} data-testid="booking-notify-edit">
          {t('reception.notify.editText')}
        </Button>
      ) : null}
      {editing ? (
        <div data-testid="booking-notify-text" style={{ marginTop: 8 }}>
          <textarea
            value={text}
            onChange={(e) => onTextChange(e.target.value)}
            maxLength={NOTIFY_BODY_MAX}
            rows={3}
            dir="auto"
            aria-label={t('reception.notify.textLabel')}
            data-testid="booking-notify-textarea"
            style={{
              width: '100%', boxSizing: 'border-box', resize: 'vertical',
              border: `1px solid ${C.border}`, borderRadius: 10, padding: '8px 10px',
              fontSize: '0.8125rem', lineHeight: 1.8, fontFamily: 'inherit',
              color: C.textPrimary, background: C.bg,
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, fontSize: '0.71875rem', color: C.textMuted, lineHeight: 1.7 }}>
            <span>{t('reception.notify.textHint')}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
              <span data-testid="booking-notify-segments">{t('reception.notify.segments', { count: formatDigits(parts) })}</span>
              <Button variant="link" size="sm" onClick={() => onTextChange(null)} data-testid="booking-notify-reset">
                {t('reception.notify.resetText')}
              </Button>
            </span>
          </div>
        </div>
      ) : null}
    </FormField>
  );
}
